import { faSave } from "@fortawesome/pro-solid-svg-icons"
import { Box, Grid, Typography, useTheme } from "@mui/material"
import IconForButton from "components/ButtonIcon"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { Formik, FormikValues } from "formik"
import React, { useEffect, useState } from "react"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi3"
import ClpButton from "components/Button"
import { apiEndpoints } from "generated/apiEndpoints"
import { IFormAreaProps } from "../FormArea/formarea.interface"
import FormComponents from "../FormComponents/FormComponents"
import { IFormFieldProps } from "../FormField/formfield.interface"
import { IFormGroupProps } from "../FormGroup/formgroup.interface"
import { IFormAnswer, IFormTemplateEditProps } from "./formtemplate.interface"
import { useTranslation } from "react-i18next"
import { areDependenciesMet } from "components/Form/FormTemplate/formtemplate.helpers"
import { getValidation } from "./validation"
import * as Yup from "yup"

const EditFormTemplate: React.FC<IFormTemplateEditProps> = (props) => {
  const {
    id,
    formTemplateId,
    formAreas,
    formAnswers,
    setEditForm,
    translationData,
    insuranceCompletedFieldId
  } = props
  const [formResult, postForm] = useApi() as [IApiResult, IApiFunc]
  const [allAreas, setAllAreas] = useState<IFormAreaProps[]>([])
  const [initialValues, setInitialValues] = useState<FormikValues>({})
  const [validationSteps, setValidationSteps] = useState<any>([])
  const { t } = useTranslation("translation")
  const theme = useTheme()
  const activeStep = 0

  useEffect(() => {
    const newValidationSteps = formAreas?.map((area: IFormAreaProps) => {
      const newValidationStep = area.formGroups.flatMap((formGroup: IFormGroupProps) =>
        formGroup.formFields.map((field: IFormFieldProps) => {
          const defaultValue = formAnswers?.find(
            (x) => x.formFieldId.toString() === field.id.toString()
          )?.value
          const newObj = {
            ...initialValues,
            [field.id]: defaultValue ? defaultValue : field.value
          }
          setInitialValues(Object.assign(initialValues, newObj))

          return {
            [field.id]: getValidation(
              field.dataType,
              field.optional,
              field.labelDefault,
              t,
              field.labelKey,
              translationData
            )
          }
        })
      )

      return Yup.object().shape(Object.assign({}, ...newValidationStep))
    })

    setValidationSteps(newValidationSteps)

    if (formAreas.length > 1) {
      const lastStep = {
        id: formAreas.length,
        labelDefault: "Preview and submit",
        labelKey: `step_label.review_and_complete`,
        step: formAreas.length,
        stepLabelKey: `step_label.review_and_complete`,
        stepLabel: "Preview and submit"
      } as IFormAreaProps
      setAllAreas([...formAreas, lastStep])
    } else {
      setAllAreas(formAreas)
    }
  }, [formAnswers, formAreas])

  useEffect(() => {
    formAreas &&
      formAreas.length > 0 &&
      formAreas.map((area: IFormAreaProps) => {
        return area.formGroups.map((formGroup: IFormGroupProps) => {
          return formGroup.formFields.map((field: IFormFieldProps) => {
            const newObj = {
              ...initialValues,
              [field.id]: field.value
            }
            return setInitialValues(Object.assign(initialValues, newObj))
          })
        })
      })
    setAllAreas(formAreas)
  }, [formAreas, initialValues])

  const handleSubmitFormArea = async (values: any) => {
    const mapValues: Array<IFormAnswer> = values
      ? Object.entries(values).map(([key, value]) => {
          const mapValue = {
            formResultId: id,
            formFieldId: key,
            value: value?.toString()
          }
          return mapValue
        })
      : []
    const postValues = {
      id: id,
      formTemplateId: formTemplateId,
      formAnswers: mapValues
    }
    postForm.post(apiEndpoints.form.createorupdateformresult, postValues)
  }

  useEffect(() => {
    if (formResult.status === 2) {
      setEditForm(false)
    }
  }, [formResult])

  return Object.keys(initialValues).length !== 0 ? (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => handleSubmitFormArea(values)}
      enableReinitialize={true}
      validationSchema={validationSteps[activeStep]}>
      {(props) => {
        const { values, handleSubmit } = props
        return (
          <Box>
            <form id="CustomFieldForm" onSubmit={handleSubmit} noValidate>
              <>
                {allAreas.map((area: IFormAreaProps) => {
                  const formAreaText = t(`${translationData?.formTypeKey}.${area.labelKey}`, area.labelDefault || "")

                  return (
                    <Box marginBottom={2}>
                      {formAreaText && (
                        <Typography variant="h2" color={(theme.palette as any).grey[900]} gutterBottom>
                          {t(formAreaText)}
                        </Typography>
                      )}

                      {area?.formGroups?.map((formGroup: IFormGroupProps) => {
                        const formGroupText = t(
                          `${translationData?.formTypeKey}.${formGroup.labelKey}`,
                          formGroup.labelDefault || ""
                        )

                        return (
                          <Box marginBottom={3}>
                            {formGroupText && (
                              <Typography variant="h3" gutterBottom marginBottom="20px">
                                {t(formGroupText)}
                              </Typography>
                            )}
                            {formGroup.formFields.map((field: IFormFieldProps) => {
                              const dependenciesSatisfied = areDependenciesMet(field, area, values)
                              const insuranceCompanyField = field.id === insuranceCompletedFieldId
                              if (insuranceCompanyField) return null

                              return (
                                dependenciesSatisfied && (
                                  <FormComponents
                                    initialValues={initialValues}
                                    handleNext={() => handleSubmitFormArea(values)}
                                    field={field}
                                    formikProps={props}
                                    translationData={translationData} />
                                )
                              )
                            })}
                          </Box>
                        )
                      })}
                    </Box>
                  )
                })}

                <Grid container direction="row" gap="2px" justifyContent="center" mt={5} mb={5}>
                  <Grid item>
                    <ClpButton
                      type="submit"
                      color="primary"
                      variant="contained"
                      result={formResult}
                      startIcon={<IconForButton icon={faSave as IconProp} color="primary" />}>
                      {t("global.buttons.save", "Save")}
                    </ClpButton>
                  </Grid>
                </Grid>
              </>
            </form>
          </Box>
        )
      }}
    </Formik>
  ) : (
    <></>
  )
}

export default EditFormTemplate
